/**
 * motion.js — токены движения и правила темпа.
 *
 * Модель IBM Carbon: длительность и плавность заданы константами, а не
 * подбираются на каждый кадр. Это даёт единство сотен роликов без
 * художественного решения в каждом сценарии.
 *
 * Два стиля движения:
 *   productive — рабочий интерфейс: быстро, по делу (курсор, клик, панель)
 *   expressive — смысловые моменты: медленнее, заметнее (появление объекта,
 *                облёт камеры, подсветка)
 *
 * ⚠️ ТЕМП ЗАДАН ИССЛЕДОВАНИЯМИ, не вкусом:
 *   • курсор ВДВОЕ медленнее естественного — быстрый курсор это причина №1
 *     перемоток назад (van der Meij, Univ. of Twente);
 *   • пауза ~2 секунды после каждого действия — чтобы усвоить произошедшее;
 *   • паузы ПРИНУДИТЕЛЬНЫЕ: ученики почти не жмут паузу сами
 *     (пересматривают 6.7% времени, 20% видео пропускают совсем).
 */

/** Длительности, мс. */
export const DUR = {
    // productive — рабочие движения интерфейса
    fast: 180,        // мелкая реакция: нажатие, подсветка кнопки
    moderate: 340,    // панель выехала, значение изменилось
    slow: 620,        // курсор едет на короткое расстояние

    // expressive — смысловые моменты
    travel: 900,      // курсор едет через весь экран (вдвое медленнее «нормы»)
    appear: 520,      // объект появляется на сцене
    move3d: 780,      // объект едет по сцене
    orbit: 1500,      // облёт камеры
};

/** Паузы, мс. Отдельно от длительностей — это не движение, а время на подумать. */
export const PAUSE = {
    /** После действия — обязательная, из руководств по обучению софту. */
    afterAction: 1200,
    /** Перед действием — дать заметить подсветку цели. */
    beforeAction: 700,
    /** Между смысловыми блоками кадра. */
    beat: 500,
    /** В конце кадра, прежде чем уйти к следующему. */
    endOfFrame: 900,
};

/** Плавности. */
export const EASE = {
    /** Рабочее движение — ускорился, затормозил. */
    standard: (k) => (k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2),
    /** Вход: быстро стартует, мягко приходит. Для появления объектов. */
    entrance: (k) => 1 - Math.pow(1 - k, 3),
    /** Выход: мягко трогается, быстро уходит. */
    exit: (k) => k * k * k,
    /** Лёгкий перелёт — курсор «доводит» до цели, как живая рука. */
    overshoot: (k) => {
        const c = 1.70158, c3 = c + 1;
        return 1 + c3 * Math.pow(k - 1, 3) + c * Math.pow(k - 1, 2);
    },
};

/**
 * Ограничение темпа: курсор не должен лететь быстрее порога.
 * Возвращает длительность движения по расстоянию (в процентах экрана).
 *
 * Порог взят вдвое медленнее «естественного» движения руки — именно это
 * рекомендуют исследования скринкастов.
 */
export function cursorDuration(dx, dy) {
    const dist = Math.sqrt(dx * dx + dy * dy);      // проценты экрана
    const MIN = DUR.slow;
    const MS_PER_PERCENT = 11;                       // вдвое медленнее «нормы»
    return Math.max(MIN, Math.round(dist * MS_PER_PERCENT));
}

/**
 * Сколько времени кадр должен показываться, если озвучки нет.
 * При наличии озвучки длительность берётся из неё (модель Duolingo).
 *
 * Оценка по длине текста: русская речь ~11 символов в секунду.
 */
export function estimateSpeech(text) {
    if (!text) return 1500;
    const CHARS_PER_SEC = 11;
    return Math.max(1800, Math.round((text.length / CHARS_PER_SEC) * 1000));
}

const motion = { DUR, PAUSE, EASE, cursorDuration, estimateSpeech };
export default motion;
